import React from 'react';
import { observer } from 'mobx-react';
import { SvgIcon, Box } from 'components';
import { useViewerControl } from 'libs/rendering/gltf/hooks';
import color from 'styles/themes/wikifactory/styles/colors';
import { FaceStyled } from './face.styled';

interface FaceProps {
  face: FaceObjProps;
  ix: number;
  icon?: React.ReactNode;
}

interface FaceObjProps {
  // wasm [MeshFeature]
  selfIx: number;
  GetArea(): number;
  GetSurfaceData(): string;
}

export const Face = observer(({ icon, face, ix }: FaceProps) => {
  if (!face) return null;

  const viewerControl = useViewerControl();
  const selManager = viewerControl?.viewer?.selectionManager;
  if (!selManager) return null;
  const selected = selManager.IsModelItemSelectionTarget(face);
  const highlighted = selManager.IsModelItemHighlightTarget(face);

  const area = face.GetArea();
  // const fIndex = `${feature.selfIx + 1}.`; // debug
  const fIndex = `${ix + 1}.`;
  const fArea = `${area.toFixed(1)}u²`;

  // get surface parameters data
  const surfEnum = viewerControl?.viewer?.module.SurfaceType;
  const surfJson = JSON.parse(face.GetSurfaceData());
  const { type } = surfJson;
  const extraLabels = [];
  const toRad = 180 / Math.PI;
  switch (type) {
    case surfEnum.SurfaceTypeCylinder.value:
      extraLabels.push({
        txt: '\u2300', // diameter (⌀)
        val: (2 * surfJson.radius).toFixed(1),
      });
      extraLabels.push({ txt: '𝐡', val: surfJson.height.toFixed(1) }); // height (𝐡)
      break;
    case surfEnum.SurfaceTypeCone.value:
      extraLabels.push({
        txt: '\u2300', // diameter (⌀)
        val: (2 * surfJson.radius).toFixed(1),
      });
      extraLabels.push({ txt: '𝐡', val: surfJson.height.toFixed(1) }); // height (𝐡)
      extraLabels.push({
        txt: '\u2221', // angle (∡)
        val: `${(toRad * surfJson.angle).toFixed(0)}°`,
      });
      break;
    case surfEnum.SurfaceTypeSphere.value:
      extraLabels.push({
        txt: '\u2300', // diameter (⌀)
        val: (2 * surfJson.radius).toFixed(1),
      });
      break;
    case surfEnum.SurfaceTypeTorus.value:
      extraLabels.push({
        txt: '\u2300', // diameter (⌀)
        val: (2 * surfJson.radius).toFixed(1),
      });
      extraLabels.push({ txt: '𝐑', val: surfJson.circle.r.toFixed(1) }); // inner-radius (𝐑)
      break;
    default:
      break;
  }

  /*
    render
  */

  return (
    <FaceStyled
      selected={selected}
      highlighted={highlighted}
      onMouseEnter={() => selManager.AddHighlightItem(face)}
      onMouseLeave={() => selManager.ClearHighlight()}
    >
      {icon && <SvgIcon icon={icon} size='medium' />}
      <Box
        mr={1}
        sx={{
          width: '40px',
          textAlign: 'right',
          color: color.wfGrey700,
          fontVariantNumeric: 'tabular-nums',
          fontSize: '14px',
        }}
      >
        {fIndex}
      </Box>
      {/* Surface paramters */}
      {extraLabels.map((labelObj, labelKey) => (
        <Box
          key={`extraLabel_${labelKey}`}
          mr={1}
          sx={{
            width: '60px',
            textAlign: 'center',
            borderLeft: `1px solid ${color.wfGrey400}`,
          }}
        >
          {`${labelObj.txt} ${labelObj.val}`}
        </Box>
      ))}
      {/* Surface area */}
      <Box
        mr={4}
        sx={{
          color: color.wfBlue600,
          padding: '0 5px',
          borderLeft: `1px solid ${color.wfGrey400}`,
        }}
      >
        {fArea}
      </Box>
    </FaceStyled>
  );
});
